import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { PencilSimple, Trash, Plus, SignOut, Package, BeerBottle as BeerIcon, X, Sparkle, Crown, ForkKnife, Tag, Truck, ClockCounterClockwise, WhatsappLogo, FilmSlate } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MenuAdmin } from "./MenuAdmin";
import { OffersAdmin } from "./OffersAdmin";
import { IntroVideoAdmin } from "./IntroVideoAdmin";

const EMPTY = {
  name: "", style: "", category: "artesanal", product_type: "cerveza", origin: "", abv: 5.0,
  price: 0, volume_ml: 355, unit_label: "355 ml", description: "", image_url: "", stock: 50, featured: false,
};

const FIELDS = [
  { k: "name", l: "Nombre", type: "text" },
  { k: "style", l: "Estilo (IPA, Bombones, Tueste...)", type: "text" },
  { k: "origin", l: "Origen", type: "text" },
  { k: "abv", l: "ABV % (solo cerveza)", type: "number", step: "0.1" },
  { k: "price", l: "Precio (MXN)", type: "number", step: "1" },
  { k: "unit_label", l: "Unidad (355 ml, 250 g, Caja 9 pzas)", type: "text" },
  { k: "stock", l: "Stock", type: "number", step: "1" },
  { k: "image_url", l: "URL de imagen", type: "text", full: true },
];

function BeerForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initial.id);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.style || !form.price) {
      toast.error("Nombre, estilo y precio son obligatorios");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      abv: parseFloat(form.abv) || 0,
      price: parseFloat(form.price) || 0,
      volume_ml: parseInt(form.volume_ml) || 355,
      stock: parseInt(form.stock) || 0,
      image_url: form.image_url || "https://images.unsplash.com/photo-1759306509152-9a0cee83aa30?w=800",
    };
    try {
      if (editing) await api.put(`/admin/beers/${form.id}`, payload);
      else await api.post("/admin/beers", payload);
      toast.success(editing ? "Cerveza actualizada" : "Cerveza creada");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="beer-form-modal">
      <div className="w-full max-w-2xl bg-[#141414] border border-white/15 max-h-[90vh] overflow-auto cg-scroll">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#141414]">
          <h2 className="font-display text-3xl">{editing ? "Editar producto" : "Nuevo producto"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-form"><X size={22} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {FIELDS.map((f) => (
            <div key={f.k} className={f.full ? "sm:col-span-2" : ""}>
              <label className="overline block mb-2">{f.l}</label>
              <input
                type={f.type}
                step={f.step}
                value={form[f.k]}
                onChange={(e) => set(f.k, e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white"
                data-testid={`field-${f.k}`}
              />
            </div>
          ))}
          <div>
            <label className="overline block mb-2">Tipo de producto</label>
            <select
              value={form.product_type}
              onChange={(e) => set("product_type", e.target.value)}
              className="w-full bg-[#141414] border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white"
              data-testid="field-product-type"
            >
              <option value="cerveza">Cerveza</option>
              <option value="boutique">Boutique</option>
            </select>
          </div>
          <div>
            <label className="overline block mb-2">Categoría</label>
            <select
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className="w-full bg-[#141414] border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white"
              data-testid="field-category"
            >
              <option value="artesanal">Artesanal</option>
              <option value="importada">Importada</option>
              <option value="nacional">Nacional</option>
            </select>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => set("featured", e.target.checked)}
              className="w-4 h-4 accent-[#D97736]"
              data-testid="field-featured"
              id="featured"
            />
            <label htmlFor="featured" className="text-sm text-gray-300">Destacada en inicio</label>
          </div>
          <div className="sm:col-span-2">
            <label className="overline block mb-2">Descripción</label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={3}
              className="w-full bg-transparent border border-white/20 focus:border-[#D97736] outline-none p-3 text-white text-sm"
              data-testid="field-description"
            />
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-3 border border-white/20 hover:border-white/40 uppercase tracking-wide text-sm transition-colors">Cancelar</button>
          <button onClick={save} disabled={saving} className="px-6 py-3 bg-[#D97736] hover:bg-[#E88D50] disabled:opacity-60 text-black font-semibold uppercase tracking-wide text-sm transition-colors" data-testid="save-beer">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [tab, setTab] = useState("beers");
  const [beers, setBeers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [catas, setCatas] = useState([]);
  const [mayor, setMayor] = useState([]);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(null); // beer object or EMPTY or null
  const [confirm, setConfirm] = useState(null);

  const load = () => {
    api.get("/beers").then((r) => setBeers(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/cata-requests").then((r) => setCatas(r.data)).catch(() => {});
    api.get("/admin/mayor-requests").then((r) => setMayor(r.data)).catch(() => {});
    api.get("/admin/orders/history").then((r) => setHistory(r.data)).catch(() => {});
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (ready && !user) return <Navigate to="/login" replace />;
  if (!ready) return <div className="min-h-[60vh] flex items-center justify-center text-gray-500">Cargando...</div>;

  const del = async (id) => {
    try {
      await api.delete(`/admin/beers/${id}`);
      toast.success("Cerveza eliminada");
      setConfirm(null);
      load();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const dispatchOrder = async (id) => {
    try { await api.put(`/admin/orders/${id}/dispatch`); toast.success("Pedido despachado. Toca 'Avisar por WhatsApp' para notificar al cliente."); load(); }
    catch { toast.error("Error al despachar"); }
  };
  const undispatchOrder = async (id) => {
    try { await api.put(`/admin/orders/${id}/undispatch`); toast.success("Pedido marcado como pendiente"); load(); }
    catch { toast.error("Error al actualizar"); }
  };
  const removeOrder = async (id) => {
    try { await api.delete(`/admin/orders/${id}`); toast.success("Pedido eliminado"); load(); }
    catch { toast.error("Error al eliminar"); }
  };
  const hoursLeft = (dispatchedAt) => {
    const end = new Date(dispatchedAt).getTime() + 48 * 3600 * 1000;
    return Math.max(0, Math.ceil((end - Date.now()) / 3600000));
  };
  const notifyWhatsApp = (o) => {
    let phone = (o.customer_phone || "").replace(/\D/g, "");
    if (phone.startsWith("0")) phone = "58" + phone.slice(1);
    else if (!phone.startsWith("58")) phone = "58" + phone;
    const msg = `Hola ${o.customer_name || ""}, ¡buenas noticias! 🍺 Tu pedido ${o.reference} de Ciudad Garaje ya fue despachado y va en camino. ¡Gracias por tu compra!`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="overline text-[#F5A623]">Panel de administración</div>
            <h1 className="font-display text-5xl">Ciudad Garaje</h1>
          </div>
          <button onClick={logout} className="flex items-center gap-2 border border-white/20 hover:border-[#D97736] hover:text-[#D97736] px-4 py-2.5 uppercase tracking-wide text-sm transition-colors" data-testid="logout-btn">
            <SignOut size={16} /> Salir
          </button>
        </div>

        {/* stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-8">
          {[
            { l: "Cervezas", v: beers.length },
            { l: "Destacadas", v: beers.filter((b) => b.featured).length },
            { l: "Pedidos", v: orders.length },
            { l: "Pagados", v: orders.filter((o) => o.payment_status === "paid").length },
          ].map((s) => (
            <div key={s.l} className="bg-[#111] p-5">
              <div className="font-display text-4xl text-[#D97736]">{s.v}</div>
              <div className="overline mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        {/* tabs */}
        <div className="flex gap-6 border-b border-white/10 mb-6">
          {[{ k: "beers", l: "Cervezas", icon: BeerIcon }, { k: "menu", l: "Menú", icon: ForkKnife }, { k: "offers", l: "Ofertas", icon: Tag }, { k: "intro", l: "Intro", icon: FilmSlate }, { k: "orders", l: "Pedidos", icon: Package }, { k: "history", l: "Historial", icon: ClockCounterClockwise }, { k: "catas", l: "Catas", icon: Sparkle }, { k: "mayor", l: "Mayor", icon: Crown }].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`flex items-center gap-2 pb-3 uppercase tracking-widest text-sm transition-colors ${tab === t.k ? "text-[#D97736] border-b-2 border-[#D97736]" : "text-gray-400"}`}
              data-testid={`tab-${t.k}`}
            >
              <t.icon size={16} weight="bold" /> {t.l}
            </button>
          ))}
        </div>

        {tab === "beers" && (
          <>
            <div className="flex justify-end mb-4">
              <button onClick={() => setEditing(EMPTY)} className="flex items-center gap-2 bg-[#D97736] hover:bg-[#E88D50] text-black font-semibold px-5 py-3 uppercase tracking-wide text-sm transition-colors" data-testid="add-beer-btn">
                <Plus size={16} weight="bold" /> Nuevo producto
              </button>
            </div>
            <div className="border border-white/10 overflow-x-auto cg-scroll">
              <table className="w-full text-sm" data-testid="beers-table">
                <thead>
                  <tr className="bg-[#111] text-left">
                    {["", "Nombre", "Tipo", "Estilo", "Categoría", "Precio", "Stock", ""].map((h, i) => (
                      <th key={i} className="p-3 overline">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {beers.map((b) => (
                    <tr key={b.id} className="border-t border-white/10 hover:bg-white/5" data-testid={`beer-row-${b.id}`}>
                      <td className="p-3"><img src={b.image_url} alt="" className="w-10 h-12 object-cover border border-white/10" /></td>
                      <td className="p-3 font-medium">{b.name}{b.featured && <span className="ml-2 text-[#F5A623] text-xs">★</span>}</td>
                      <td className="p-3"><span className="capitalize text-[#D97736]">{b.product_type}</span></td>
                      <td className="p-3 text-gray-400">{b.style}</td>
                      <td className="p-3"><span className="capitalize text-gray-400">{b.category}</span></td>
                      <td className="p-3 text-[#D97736] font-semibold">{money(b.price)}</td>
                      <td className="p-3 text-gray-400">{b.stock}</td>
                      <td className="p-3">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setEditing(b)} className="w-9 h-9 flex items-center justify-center border border-white/15 hover:border-[#D97736] hover:text-[#D97736] transition-colors" data-testid={`edit-${b.id}`}>
                            <PencilSimple size={16} />
                          </button>
                          <button onClick={() => setConfirm(b)} className="w-9 h-9 flex items-center justify-center border border-white/15 hover:border-red-500 hover:text-red-400 transition-colors" data-testid={`delete-${b.id}`}>
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "menu" && <MenuAdmin />}

        {tab === "offers" && <OffersAdmin />}

        {tab === "intro" && <IntroVideoAdmin />}

        {tab === "orders" && (
          <div className="border border-white/10 overflow-x-auto cg-scroll">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead>
                <tr className="bg-[#111] text-left">
                  {["Pedido", "Cliente", "Contacto", "Método", "Total", "Estado", "Fecha", "Seguimiento"].map((h) => (
                    <th key={h} className="p-3 overline">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-gray-500">Aún no hay pedidos.</td></tr>
                )}
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-white/10">
                    <td className="p-3 text-[#D97736] font-medium">{o.reference || "—"}</td>
                    <td className="p-3">{o.customer_name || o.customer_email || "—"}</td>
                    <td className="p-3 text-gray-400 text-xs">
                      <div>{o.customer_phone || "—"}</div>
                      <div className="break-all">{o.customer_email || ""}</div>
                    </td>
                    <td className="p-3">
                      <span className="capitalize text-gray-300">{(o.method || "—").replace("_", " ")}</span>
                      {o.payment_reference && <div className="text-[#F5A623] text-xs mt-1" data-testid={`order-ref-${o.id}`}>Comprobante: {o.payment_reference}</div>}
                    </td>
                    <td className="p-3 text-[#D97736] font-semibold">{money(o.amount)}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs uppercase tracking-wide ${o.payment_status === "paid" ? "text-[#25D366] border border-[#25D366]/40" : "text-[#F5A623] border border-[#F5A623]/40"}`}>
                        {o.payment_status}
                      </span>
                    </td>
                    <td className="p-3 text-gray-500">{o.created_at?.slice(0, 10)}</td>
                    <td className="p-3">
                      {o.dispatched_at ? (
                        <div className="flex flex-col gap-1.5" data-testid={`order-tracking-${o.id}`}>
                          <span className="inline-flex items-center gap-1.5 text-[#25D366] text-xs uppercase tracking-wide font-medium">
                            <Truck size={15} weight="fill" /> Despachado
                          </span>
                          <span className="text-gray-500 text-xs">Desaparece en {hoursLeft(o.dispatched_at)} h</span>
                          <button onClick={() => notifyWhatsApp(o)} className="inline-flex items-center gap-1.5 text-[#25D366] hover:text-[#1eb955] text-xs font-medium w-fit" data-testid={`notify-wa-${o.id}`}>
                            <WhatsappLogo size={14} weight="fill" /> Avisar por WhatsApp
                          </button>
                          <button onClick={() => undispatchOrder(o.id)} className="text-gray-500 hover:text-[#F5A623] text-xs underline w-fit" data-testid={`undispatch-${o.id}`}>Deshacer</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button onClick={() => dispatchOrder(o.id)} className="flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1eb955] text-black font-semibold px-3 py-2 text-xs uppercase tracking-wide transition-colors" data-testid={`dispatch-${o.id}`}>
                            <Truck size={15} weight="bold" /> Despachar
                          </button>
                          <button onClick={() => removeOrder(o.id)} className="w-9 h-9 flex items-center justify-center border border-white/15 hover:border-red-500 hover:text-red-400 text-gray-400 transition-colors" title="Eliminar pedido" data-testid={`del-order-${o.id}`}>
                            <Trash size={15} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "history" && (
          <div className="border border-white/10 overflow-x-auto cg-scroll">
            <table className="w-full text-sm" data-testid="history-table">
              <thead>
                <tr className="bg-[#111] text-left">
                  {["Pedido", "Cliente", "Método", "Comprobante", "Total", "Estado", "Despachado", "Fecha"].map((h) => (
                    <th key={h} className="p-3 overline">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-gray-500">Aún no hay pedidos en el historial.</td></tr>
                )}
                {history.map((o) => (
                  <tr key={o.id} className="border-t border-white/10">
                    <td className="p-3 text-[#D97736] font-medium">{o.reference || "—"}</td>
                    <td className="p-3">{o.customer_name || o.customer_email || "—"}<div className="text-gray-500 text-xs">{o.customer_phone}</div></td>
                    <td className="p-3 capitalize text-gray-300">{(o.method || "—").replace("_", " ")}</td>
                    <td className="p-3 text-[#F5A623] text-xs">{o.payment_reference || "—"}</td>
                    <td className="p-3 text-[#D97736] font-semibold">{money(o.amount)}</td>
                    <td className="p-3">
                      {o.dispatched_at ? (
                        <span className="inline-flex items-center gap-1.5 text-[#25D366] text-xs uppercase tracking-wide"><Truck size={14} weight="fill" /> Despachado</span>
                      ) : (
                        <span className="text-[#F5A623] text-xs uppercase tracking-wide">Pendiente</span>
                      )}
                    </td>
                    <td className="p-3 text-gray-500 text-xs">{o.dispatched_at ? o.dispatched_at.slice(0, 10) : "—"}</td>
                    <td className="p-3 text-gray-500">{o.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "catas" && (
          <div className="border border-white/10 overflow-x-auto cg-scroll">
            <table className="w-full text-sm" data-testid="catas-table">
              <thead>
                <tr className="bg-[#111] text-left">
                  {["Solicitud", "Nombre", "Contacto", "Evento", "Fecha", "Personas", "Mensaje"].map((h) => (
                    <th key={h} className="p-3 overline">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {catas.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-gray-500">Aún no hay solicitudes de cata.</td></tr>
                )}
                {catas.map((c) => (
                  <tr key={c.id} className="border-t border-white/10 align-top">
                    <td className="p-3 text-[#D97736] font-medium whitespace-nowrap">{c.reference}</td>
                    <td className="p-3">{c.name}</td>
                    <td className="p-3 text-gray-400 text-xs">
                      <div>{c.phone}</div>
                      <div className="break-all">{c.email}</div>
                    </td>
                    <td className="p-3 capitalize text-gray-300">{c.event_type}</td>
                    <td className="p-3 text-gray-400">{c.event_date || "—"}</td>
                    <td className="p-3 text-gray-400">{c.guests || "—"}</td>
                    <td className="p-3 text-gray-400 text-xs max-w-xs">{c.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "mayor" && (
          <div className="border border-white/10 overflow-x-auto cg-scroll">
            <table className="w-full text-sm" data-testid="mayor-table">
              <thead>
                <tr className="bg-[#111] text-left">
                  {["Solicitud", "Nombre", "Negocio", "Contacto", "Cantidad", "Mensaje"].map((h) => (
                    <th key={h} className="p-3 overline">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mayor.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-gray-500">Aún no hay solicitudes al mayor.</td></tr>
                )}
                {mayor.map((m) => (
                  <tr key={m.id} className="border-t border-white/10 align-top">
                    <td className="p-3 text-[#D97736] font-medium whitespace-nowrap">{m.reference}</td>
                    <td className="p-3">{m.name}</td>
                    <td className="p-3 text-gray-400">{m.company || "—"}</td>
                    <td className="p-3 text-gray-400 text-xs">
                      <div>{m.phone}</div>
                      <div className="break-all">{m.email}</div>
                    </td>
                    <td className="p-3 text-white font-semibold">{m.quantity}</td>
                    <td className="p-3 text-gray-400 text-xs max-w-xs">{m.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <BeerForm initial={editing} onClose={() => setEditing(null)} onSaved={load} />}

      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#141414] border border-white/15 p-6 max-w-sm w-full" data-testid="delete-confirm">
            <h3 className="font-display text-3xl">¿Eliminar producto?</h3>
            <p className="text-gray-400 text-sm mt-2">Vas a eliminar <span className="text-white">{confirm.name}</span>. Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setConfirm(null)} className="px-5 py-3 border border-white/20 uppercase tracking-wide text-sm">Cancelar</button>
              <button onClick={() => del(confirm.id)} className="px-5 py-3 bg-red-600 hover:bg-red-500 text-white uppercase tracking-wide text-sm transition-colors" data-testid="confirm-delete">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
